import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { ChallengeMe, ChallengeSummary, ChallengeTeam, ChallengeTeamRequest, TEAM_REQUEST_TEXTS, challengeTeamIntent, teamFriendAction } from '@radevents/shared';
import ChallengeDetailHeader from '../components/winterchallenge/ChallengeDetailHeader';
import { TeamView } from '../components/winterchallenge/TeamParts';
import { Spinner } from '../components/ui';
import { messageOf } from '../components/winterchallenge/messageOf';
import { useAuth } from '../contexts/AuthContext';
import challengeService from '../services/challengeService';

/**
 * `/winterchallenge/team/:slug` — öffentlich. Mit `?code=` aus dem Einladungslink kann man
 * beitreten, ohne Link anfragen; wer noch nicht angemeldet ist oder noch nicht mitmacht, wird erst
 * dorthin geschickt und ist danach im Team bzw. hat angefragt. Der Gründer nimmt hier Anfragen an.
 */
const WinterchallengeTeam: React.FC = () => {
  const { slug = '' } = useParams();
  const [params] = useSearchParams();
  const code = params.get('code');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [team, setTeam] = useState<ChallengeTeam | null>(null);
  const [friendBusy, setFriendBusy] = useState<number | null>(null);
  const [missing, setMissing] = useState(false);
  const [me, setMe] = useState<ChallengeMe | null>(null);
  const [meLoaded, setMeLoaded] = useState(false);
  const [summary, setSummary] = useState<ChallengeSummary | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    challengeService.team(slug).then(setTeam).catch(() => setMissing(true));
  }, [slug, isAuthenticated]);
  useEffect(() => {
    if (!isAuthenticated) { setMe(null); setMeLoaded(true); return; }
    setMeLoaded(false);
    challengeService.me().then(setMe).catch(() => setMe(null)).finally(() => setMeLoaded(true));
  }, [isAuthenticated]);
  // Wer noch nicht mitmacht, liest erst, worum es geht, ob mit Einladungslink oder ohne.
  useEffect(() => {
    if (meLoaded && !me) challengeService.summary().then(setSummary).catch(() => setSummary(null));
  }, [meLoaded, me]);
  const intro = meLoaded && !me ? summary : null;

  const participationPath = `/winterchallenge?mitmachen=1&team=${encodeURIComponent(slug)}&code=${encodeURIComponent(code ?? '')}`;
  const requestPath = `/winterchallenge?mitmachen=1${challengeTeamIntent(slug, null, '1')?.query ?? ''}`;

  /** Ein Abruf, der die Teamseite neu liefert; der Knopf nimmt solange keinen zweiten Klick an. */
  const run = async (call: () => Promise<ChallengeTeam>, success: string | null, failure: string) => {
    setBusy(true);
    try {
      setTeam(await call());
      if (success) toast.success(success);
    } catch (err) {
      toast.error(messageOf(err, failure));
      challengeService.team(slug).then(setTeam).catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const request = () => {
    if (!isAuthenticated) { navigate(`/login?redirect=${encodeURIComponent(requestPath)}`); return; }
    if (!me) {
      toast('Mach kurz bei der Winterchallenge mit, danach ist deine Anfrage gestellt.');
      navigate(requestPath);
      return;
    }
    run(() => challengeService.requestTeam(slug), TEAM_REQUEST_TEXTS.sent(team?.name ?? 'das Team'), 'Die Anfrage ist nicht angekommen.');
  };
  const withdraw = () => run(() => challengeService.withdrawTeamRequest(slug), 'Anfrage zurückgezogen', 'Die Anfrage ließ sich nicht zurückziehen.');
  const decide = (r: ChallengeTeamRequest, accept: boolean) => {
    // Ablehnen ist endgültig: dasselbe Team lässt sich danach nicht noch einmal anfragen.
    if (!accept && !window.confirm(`Anfrage von ${r.displayName} ablehnen?\n\nEine abgelehnte Anfrage lässt sich nicht noch einmal an euer Team stellen.`)) return;
    return run(
    () => challengeService.decideTeamRequest(slug, r.id, accept),
    accept ? TEAM_REQUEST_TEXTS.accepted(r.displayName) : TEAM_REQUEST_TEXTS.declinedToast,
    accept ? 'Annehmen hat nicht geklappt.' : 'Ablehnen hat nicht geklappt.',
    );
  };

  const join = async () => {
    if (!isAuthenticated) { navigate(`/login?redirect=${encodeURIComponent(participationPath)}`); return; }
    if (!me) {
      toast('Mach kurz bei der Winterchallenge mit, danach bist du gleich im Team.');
      navigate(participationPath);
      return;
    }
    try {
      setTeam(await challengeService.joinTeam(slug, code ?? ''));
      toast.success('Du fährst jetzt im Team');
    } catch (err) {
      toast.error(messageOf(err, 'Der Beitritt hat nicht geklappt.'));
    }
  };

  const friend = async (member: ChallengeTeam['members'][number]) => {
    if (!member.friendshipState || friendBusy != null) return;
    const action = teamFriendAction(member.friendshipState);
    if (!action) return;
    setFriendBusy(member.participantId);
    try { setTeam(await challengeService.teamFriend(slug, member.participantId, action)); }
    catch (e) { toast.error(messageOf(e, 'Die Freundschaftsanfrage hat nicht geklappt.')); }
    finally { setFriendBusy(null); }
  };

  return (
    <div className="has-bottom-nav" style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
      <Helmet>
        <title>{team ? `${team.name} | Winterchallenge | Radsport Events` : 'Team | Winterchallenge | Radsport Events'}</title>
        <meta name="description" content={team ? `${team.name} sammelt Kilometer in der Winterchallenge. Mitfahren geht über den Einladungslink oder eine Beitrittsanfrage.` : 'Ein Team der Winterchallenge auf Radsport Events.'} />
        {/* Einladungslinks: die Teamseite gehört nicht in den Index. */}
        <meta name="robots" content="noindex" />
      </Helmet>
      <ChallengeDetailHeader />
      <main style={{ flex: 1 }}>
        {team ? (
          <TeamView onFriend={friend} friendBusy={friendBusy} team={team} inviteCode={code} intro={intro} busy={busy}
            currentTeamName={me?.team && me.team.slug !== team.slug ? me.team.name : null}
            onJoin={join} onRequest={request} onWithdraw={withdraw} onDecide={decide} />
        ) : missing ? (
          <p className="wc-empty" style={{ maxWidth: 560, margin: '60px auto' }}>Dieses Team gibt es nicht (mehr).</p>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Spinner /></div>
        )}
      </main>
    </div>
  );
};

export default WinterchallengeTeam;
